import React, { useState, useRef } from 'react';
import Button from './Button';

const FileUpload = ({ onUpload, accept = '*', maxSize = 10485760, multiple = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    // Validate file size
    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum size of ${(maxSize / 1048576).toFixed(0)}MB`);
      return;
    }

    setUploading(true);
    try {
      if (multiple) {
        await onUpload(files);
      } else {
        await onUpload(files[0]);
      }
      // Clear input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const containerStyle = {
    border: `2px dashed ${isDragging ? 'var(--primary)' : 'var(--border)'}`,
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    background: isDragging ? 'var(--primary-light)' : 'var(--surface-light)',
    transition: 'all 0.3s ease',
    cursor: uploading ? 'not-allowed' : 'pointer'
  };

  const iconStyle = {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5
  };

  const titleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginBottom: '16px'
  };

  return (
    <div>
      <div
        style={containerStyle}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <div style={iconStyle}></div>
        <div style={titleStyle}>
          {uploading ? 'Uploading...' : 'Drop files here or click to browse'}
        </div>
        <div style={subtitleStyle}>
          {multiple ? 'You can upload multiple files' : 'Upload a file'} (Max {(maxSize / 1048576).toFixed(0)}MB)
        </div>
        <Button variant="primary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Choose File'}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept={accept}
        multiple={multiple}
        disabled={uploading}
      />
    </div>
  );
};

export default FileUpload;